import API from "../api";
import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";

// 🟢 Haptic Feedback Engine
const vibrate = (pattern) => {
  if (typeof window !== "undefined" && navigator.vibrate) {
    navigator.vibrate(pattern);
  }
};

function ForgotPassword() {
  const [step, setStep] = useState(1); // Step 1: Request OTP | Step 2: Verify & Reset
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // 🟢 STEP 1: Request Password Reset OTP
  const handleRequestOTP = async (e) => {
    e.preventDefault();

    if (phone.length !== 10) {
      vibrate([100, 50, 100]); // 📳 Error tap
      return toast.error("Please enter a valid 10-digit mobile number");
    }

    try {
      setLoading(true);
      // Calls your backend to send an OTP via SMS or Email linked to this phone
      await API.post("/auth/forgot-password", { phone });
      
      vibrate(50); // 📳 Success tap
      toast.success("OTP sent successfully!");
      setStep(2); // Move to Step 2 UI

    } catch (err) {
      vibrate([100, 50, 100]);
      toast.error(err.response?.data?.message || "Failed to find account.");
    } finally {
      setLoading(false);
    }
  };

  // 🟢 STEP 2: Verify OTP and set New Password
  const handleResetPassword = async (e) => {
    e.preventDefault();

    if (!otp || !newPassword) {
      vibrate([100, 50, 100]);
      return toast.error("Please fill in all fields.");
    }

    if (newPassword.length < 6) {
      vibrate([100, 50, 100]);
      return toast.error("Password must be at least 6 characters.");
    }

    try {
      setLoading(true);
      // Calls your backend to verify the OTP and update the database
      await API.post("/auth/reset-password", { phone, otp, newPassword });

      vibrate([50, 50, 50]); // 📳 Success burst!
      toast.success("Password reset successful! Please log in.");
      
      // Redirect back to login after a short delay
      setTimeout(() => {
        navigate("/login");
      }, 1500);

    } catch (err) {
      vibrate([100, 50, 100]);
      toast.error(err.response?.data?.message || "Invalid OTP or reset failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={pageContainer}>
      <Toaster position="top-center" />
      <div style={authCard}>
        
        <div style={iconCircle}>🔐</div>
        <h2 style={titleStyle}>{step === 1 ? "Forgot Password?" : "Reset Password"}</h2>
        <p style={subtitleStyle}>
          {step === 1 
            ? "Enter your registered phone number to receive a reset code." 
            : `Enter the code sent to +91 ${phone} and create a new password.`}
        </p>

        {/* ================= STEP 1 UI ================= */}
        {step === 1 && (
          <form onSubmit={handleRequestOTP} className="animate-in fade-in duration-300">
            <label style={labelStyle}>Registered Phone Number</label>
            <input
              type="tel"
              placeholder="10-digit mobile number"
              value={phone}
              maxLength="10" 
              onChange={e => setPhone(e.target.value.replace(/\D/g, ""))}
              style={inputStyle}
              required
            />

            <button type="submit" disabled={loading} style={{...submitBtn, opacity: loading ? 0.7 : 1}}>
              {loading ? "Sending OTP..." : "Send Reset Code"}
            </button>
          </form>
        )}

        {/* ================= STEP 2 UI ================= */}
        {step === 2 && (
          <form onSubmit={handleResetPassword} className="animate-in slide-in-from-right duration-300">
            <label style={labelStyle}>6-Digit OTP</label>
            <input
              type="text"
              placeholder="Enter OTP"
              value={otp}
              maxLength="6"
              onChange={e => setOtp(e.target.value)}
              style={inputStyle}
              required
            />

            <label style={labelStyle}>New Password</label>
            <input
              type="password"
              placeholder="Choose a strong password"
              value={newPassword}
              onChange={e => setNewPassword(e.target.value)}
              style={inputStyle}
              required
            />

            <button type="submit" disabled={loading} style={{...submitBtn, opacity: loading ? 0.7 : 1, background: "#2E7D32"}}>
              {loading ? "Resetting..." : "Update Password"}
            </button>
            
            <button 
              type="button" 
              onClick={() => { vibrate(20); setStep(1); setOtp(""); setNewPassword(""); }} 
              style={backBtn}
            >
              Wait, I need to change the phone number
            </button>
          </form>
        )}

        <div style={footerText}>
          Remembered your password? <Link to="/login" onClick={() => vibrate(20)} style={linkStyle}>Log in here</Link>
        </div>

      </div>
    </div>
  );
}

// ================= THEMATIC STYLES =================
// These precisely match your Login and Signup pages for a seamless user experience
const pageContainer = { display: "flex", justifyContent: "center", alignItems: "center", minHeight: "85vh", padding: "20px", background: "#FAFAFA", fontFamily: "'Segoe UI', Roboto, sans-serif" };
const authCard = { background: "#FFF", width: "100%", maxWidth: "400px", padding: "40px 30px", borderRadius: "16px", boxShadow: "0 10px 30px rgba(0,0,0,0.06)", border: "1px solid #E0E0E0", overflow: "hidden" };
const iconCircle = { width: "60px", height: "60px", background: "#E8F5E9", borderRadius: "50%", display: "flex", justifyContent: "center", alignItems: "center", margin: "0 auto 15px auto", fontSize: "28px" };
const titleStyle = { color: "#333", fontSize: "26px", textAlign: "center", margin: "0 0 5px 0" };
const subtitleStyle = { color: "#666", fontSize: "14px", textAlign: "center", marginBottom: "25px", lineHeight: "1.4" };
const labelStyle = { display: "block", fontSize: "13px", fontWeight: "600", color: "#555", marginBottom: "6px", marginTop: "15px" };
const inputStyle = { width: "100%", padding: "12px 15px", borderRadius: "8px", border: "1px solid #CCC", background: "#FAFAFA", outline: "none", boxSizing: "border-box", fontSize: "15px", transition: "0.3s" };
const submitBtn = { width: "100%", padding: "14px", marginTop: "25px", background: "#F57C00", color: "#FFF", border: "none", borderRadius: "8px", fontSize: "16px", fontWeight: "bold", cursor: "pointer", boxShadow: "0 4px 12px rgba(0,0,0,0.1)", transition: "all 0.2s" };
const backBtn = { width: "100%", padding: "10px", marginTop: "15px", background: "transparent", color: "#666", border: "none", fontSize: "13px", fontWeight: "600", cursor: "pointer", textDecoration: "underline" };
const footerText = { textAlign: "center", marginTop: "25px", fontSize: "14px", color: "#666", borderTop: "1px solid #eee", paddingTop: "20px" };
const linkStyle = { color: "#2E7D32", fontWeight: "bold", textDecoration: "none" };

export default ForgotPassword;